// src/hooks/useActiveWorkspace.ts

import { useAuth } from './useAuth';
import { useAppSelector } from '../store/store';
import { selectBootCurrentWorkspace } from '../store/slices/bootContextSlice';
import { useGetWorkspacesQuery } from '../api/workspacesApi';

/**
 * Returns the active workspace in both Central and Legacy auth modes.
 *
 * CRITICAL: In Central mode, bootContext (from switch-context) is the SOLE
 * authority for WHICH workspace is active. Backend workspaces API is still
 * queried for display data (plan, isDemo, etc.), but the active workspace
 * is determined by bootContext, NOT by tokenWorkspaceId or isActive flag.
 *
 * In Legacy mode, the backend `isActive` flag is used.
 *
 * @returns The active workspace membership object, or undefined
 */
export function useActiveWorkspace() {
  const { authSource } = useAuth();
  const isCentralMode = authSource === 'central';

  // Central mode: bootContext determines which workspace is active
  const bootCurrentWorkspace = useAppSelector(selectBootCurrentWorkspace);

  // Query backend for full workspace data (both modes need display properties)
  const { data: workspaces } = useGetWorkspacesQuery();

  if (isCentralMode) {
    // Use bootContext workspace ID to find the full workspace data from backend
    if (!bootCurrentWorkspace) return undefined;
    const fullWorkspace = workspaces?.find(
      (ws) => ws.workspace.id === bootCurrentWorkspace.id
    );
    return fullWorkspace;
  }

  // Legacy mode: use isActive flag
  return workspaces?.find((ws) => ws.isActive);
}

/**
 * Returns just the active workspace ID.
 * Convenience wrapper around useActiveWorkspace().
 *
 * @returns The active workspace ID, or undefined if no workspace is active
 */
export function useActiveWorkspaceId(): string | undefined {
  const activeWorkspace = useActiveWorkspace();
  return activeWorkspace?.workspace.id;
}
